import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { History, RotateCcw, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fdatetime } from "@/lib/format";
import { useAuth, can } from "@/context/AuthContext";
import { BRAND_DEFAULTS, Loader, PageTitle } from "@/components/common";
import { DemoData } from "@/pages/console/Phase1C";

function SettingRow({ s, editable, onSaved }) {
  const [val, setVal] = useState(s.type === "list" ? s.value.join("\n") : String(s.value));
  const [showHist, setShowHist] = useState(false);
  const dirty = val !== (s.type === "list" ? s.value.join("\n") : String(s.value));
  const save = async () => {
    try {
      const value = s.type === "list" ? val.split("\n").map((v) => v.trim()).filter(Boolean) : s.type === "int" ? val.replace(/\s/g, "") : val;
      await api.put(`/admin/settings/${s.key}`, { value });
      toast.success("Paramètre enregistré");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const field = s.type === "bool"
    ? <label className="flex items-center gap-2 text-sm min-h-[44px]"><input type="checkbox" data-testid={`setting-input-${s.key}`} className="w-5 h-5" checked={val === "true"} disabled={!editable} onChange={(e) => setVal(String(e.target.checked))} />{val === "true" ? "Activé" : "Désactivé"}</label>
    : s.type === "list" || s.type === "text"
    ? <textarea data-testid={`setting-input-${s.key}`} className="mr-input min-h-[96px]" value={val} disabled={!editable} onChange={(e) => setVal(e.target.value)} />
    : <div className="flex gap-2">{s.type === "color" && <span className="w-11 h-11 shrink-0 border border-mr-border rounded-[3px]" style={{ background: val }} />}
        <input data-testid={`setting-input-${s.key}`} className={`mr-input ${s.type === "int" ? "tabular" : ""}`} value={val} disabled={!editable} onChange={(e) => setVal(e.target.value)} /></div>;
  return (
    <div className="py-4 border-b border-mr-border grid md:grid-cols-[1fr_1.3fr] gap-3" data-testid={`setting-row-${s.key}`}>
      <div>
        <p className="text-sm font-medium">{s.label} {s.critical && <span className="text-xs text-mr-warning ml-1">critique</span>}</p>
        <p className="text-xs text-mr-muted font-mono mt-0.5">{s.key}</p>
        {s.history.length > 0 && <button data-testid={`setting-history-${s.key}`} className="text-xs text-mr-text2 mt-1 inline-flex items-center gap-1 hover:text-mr-text" onClick={() => setShowHist(!showHist)}><History size={12} /> Historique ({s.history.length})</button>}
        {showHist && <ul className="mt-2 space-y-1 text-xs text-mr-muted">{s.history.slice().reverse().map((h, i) => <li key={i}>{fdatetime(h.changed_at)} · {h.changed_by_name} · ancienne valeur : {JSON.stringify(h.value).slice(0, 60)}</li>)}</ul>}
      </div>
      <div className="space-y-2">
        {field}
        {dirty && editable && <button data-testid={`setting-save-${s.key}`} className="mr-btn-primary min-h-[38px]" onClick={save}>Enregistrer</button>}
      </div>
    </div>
  );
}

function BrandAssetRow({ s, editable, onSaved }) {
  const [busy, setBusy] = useState(false);
  const url = s.value || BRAND_DEFAULTS[s.key];
  const onDark = !s.key.endsWith("_light") && !s.key.startsWith("brand.email");
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/brand/${s.key}`, fd);
      toast.success("Fichier remplacé");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const reset = async () => {
    try {
      await api.delete(`/admin/brand/${s.key}`);
      toast.success("Fichier par défaut rétabli");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="py-4 border-b border-mr-border grid md:grid-cols-[1fr_1.3fr] gap-3" data-testid={`setting-row-${s.key}`}>
      <div>
        <p className="text-sm font-medium">{s.label}</p>
        <p className="text-xs text-mr-muted font-mono mt-0.5">{s.key}</p>
        <p className="text-xs text-mr-muted mt-1">{s.value ? "Fichier téléversé" : "Fichier fourni par défaut"}</p>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="h-20 min-w-[120px] px-5 grid place-items-center rounded-[3px] border border-mr-border" style={{ background: onDark ? "#1C1C1C" : "#FFFFFF" }}>
          <img src={url} alt={s.label} className="max-h-12 max-w-[220px] object-contain" data-testid={`brand-preview-${s.key}`} />
        </div>
        {editable && <label className="mr-btn-secondary min-h-[38px] cursor-pointer" data-testid={`brand-upload-${s.key}`}>
          <Upload size={15} /> {busy ? "Envoi…" : "Remplacer"}
          <input type="file" accept=".png,.svg,.ico,.webp,.jpg,.jpeg" className="hidden" data-testid={`brand-file-${s.key}`} onChange={(e) => upload(e.target.files[0])} />
        </label>}
        {editable && s.value && <button className="mr-btn-ghost min-h-[38px]" data-testid={`brand-reset-${s.key}`} onClick={reset}><RotateCcw size={15} /> Par défaut</button>}
      </div>
    </div>
  );
}

export function SettingsPage() {
  const { me } = useAuth();
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/settings").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
  }, []);
  if (!items) return <Loader />;
  const groups = [...new Set(items.map((s) => s.group))];
  return (
    <div className="mr-fade-up">
      <PageTitle eyebrow="Paramètres" title="Paramètres du club" />
      <DemoData />
      <p className="text-sm text-mr-text2 mb-6">Montants en FCFA entiers. Chaque modification est historisée et journalisée. Les paramètres critiques sont réservés au fondateur.</p>
      {groups.map((g) => (
        <section key={g} className="mb-10">
          <h2 className="mr-eyebrow mb-2">{g}</h2>
          {items.filter((s) => s.group === g).map((s) => {
            const editable = can(me, "settings:update") && (!s.critical || can(me, "settings:critical"));
            const Row = BRAND_DEFAULTS[s.key] ? BrandAssetRow : SettingRow;
            return <Row key={`${s.key}-${s.updated_at}`} s={s} onSaved={load} editable={editable} />;
          })}
        </section>
      ))}
    </div>
  );
}

export function Docs() {
  const [md, setMd] = useState("");
  useEffect(() => {
    api.get("/admin/docs").then(({ data }) => setMd(data.markdown));
  }, []);
  if (!md) return <Loader />;
  return (
    <div className="mr-fade-up max-w-3xl" data-testid="tech-docs">
      <article className="space-y-4 text-[15px] text-mr-text2 [&_h1]:font-display [&_h1]:font-bold [&_h1]:text-3xl [&_h1]:text-mr-text [&_h2]:font-display [&_h2]:font-bold [&_h2]:text-xl [&_h2]:text-mr-text [&_h2]:mt-10 [&_h3]:font-semibold [&_h3]:text-mr-text [&_h3]:mt-6 [&_code]:font-mono [&_code]:text-xs [&_code]:bg-mr-raised [&_code]:px-1 [&_pre]:bg-mr-surface [&_pre]:border [&_pre]:border-mr-border [&_pre]:p-4 [&_pre]:overflow-x-auto [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_table]:w-full [&_td]:border [&_td]:border-mr-border [&_td]:p-2 [&_th]:border [&_th]:border-mr-border [&_th]:p-2 [&_a]:text-mr-accent">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{md}</ReactMarkdown>
      </article>
    </div>
  );
}
